/**
 * manager-anamnesis.js
 * Questionário de anamnese obrigatório antes do Add to Cart, para produtos
 * configurados com o metaobject "Anamnese" (product.metafields.custom.anamnese).
 * Expõe a classe window.AnamneseModal (instanciada por manager-pdp.js).
 *
 * Dependências (devem carregar antes):
 *   - window.DrawerManager (manager-drawer.js) — abre/fecha, ESC, overlay,
 *     focus trap e scroll lock são todos herdados do DrawerManager existente.
 *
 * ─── Por que este arquivo é separado de manager-pdp.js ───────────────────────
 *
 *   Este é um subsistema com responsabilidade própria (validar, renderizar
 *   perguntas dinâmicas, upload, chamada de rede) e não deve inflar o arquivo
 *   do PDP. manager-pdp.js apenas instancia AnamneseModal e o consulta antes
 *   de liberar o Product Form original — ele não conhece perguntas, tipos,
 *   nem a chamada de rede.
 *
 * ─── Responsabilidades (equivalente a componentes, em classes) ───────────────
 *
 *   AnamneseService     → chamada de rede ao App Proxy (POST multipart)
 *   AnamneseAttachment  → input de arquivo (receita médica): valida extensão,
 *                         MIME (quando possível) e tamanho; expõe o File
 *   AnamneseQuestion    → uma pergunta: renderiza o campo pelo "type" via um
 *                         registro extensível (QUESTION_RENDERERS) e expõe
 *                         isAnswered()/getAnswer()/reset()
 *   AnamneseForm        → orquestra as AnamneseQuestion + declaração +
 *                         AnamneseAttachment; calcula respostas/estado geral
 *   AnamneseModal        → liga o DrawerManager + AnamneseForm + AnamneseService,
 *                         controla as fases da UI (form/submitting/rejected/
 *                         error/approved) e expõe a API pública open(...)
 *
 * ─── Contrato do metaobject "Anamnese" (product.metafields.custom.anamnese) ──
 *
 *   Ver docs/managers/manager-anamnesis.md e o cabeçalho de
 *   snippets/anamnesis-modal.liquid para o mapeamento completo dos campos.
 *
 *   IMPORTANTE: "approved_answer" de uma pergunta médica (question_id
 *   diferente de "consent") NUNCA chega ao navegador — o Liquid sempre manda
 *   null pra essas. A ÚNICA exceção é a pergunta "consent": pra ela o
 *   approved_answer real é exposto e usado só pro gate de consentimento no
 *   cliente (AnamneseQuestion.blocksFlow / AnamneseForm._syncBlockedState) —
 *   não há segredo em "recusar o consentimento impede continuar". A aprovação
 *   real da compra (as perguntas médicas de verdade) continua decidida
 *   exclusivamente pelo backend (ver AnamneseService e docs/anamnese-backend.md).
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   const modal = new AnamneseModal(panelEl);
 *   modal.name        // nome do drawer (para o DrawerManager)
 *   modal.productId    // product.id (lido de data-product-id)
 *
 *   modal.open({ variantId, onApproved(properties) })
 *     → abre o modal. Se o backend aprovar, onApproved recebe SOMENTE
 *       { _anamnese_id, _anamnese_status } (nunca as respostas completas)
 *       e então o modal se fecha e reseta sozinho.
 *     → se o cliente fechar sem aprovação (recusa, X, overlay, ESC, erro),
 *       onApproved nunca é chamado — quem chamou open() decide o que fazer.
 */

/* ─── AnamneseService ─────────────────────────────────────────────────────── */

class AnamneseService {
  /**
   * @param {string} submitUrl
   * @param {{ networkError: string, unexpectedResponse: string, rejectedDefault: string, genericError: string }} messages
   *   Todo texto vem do Liquid via `t` filter (ver data-msg-* em [data-anamnesis-panel]) — nada fixo aqui.
   */
  constructor(submitUrl, messages) {
    this._url = submitUrl || '/apps/anamnese/submit';
    this._messages = messages || {};
  }

  /**
   * @param {{ productId: string, variantId: string, answers: object, declaration: boolean, attachment: File|null }} payload
   * @returns {Promise<{ status: 'approved'|'rejected'|'error', anamneseId?: string, message?: string }>}
   */
  async submit({ productId, variantId, answers, declaration, attachment }) {
    const formData = new FormData();
    formData.append('product_id', productId);
    formData.append('variant_id', variantId);
    formData.append('answers', JSON.stringify(answers));
    formData.append('declaration', declaration ? 'true' : 'false');
    if (attachment) formData.append('attachment', attachment);
    // Content-Type multipart/form-data + boundary é definido automaticamente
    // pelo navegador ao usar FormData — nunca setar manualmente.

    let res;
    try {
      res = await fetch(this._url, { method: 'POST', body: formData });
    } catch (err) {
      console.error('AnamneseService: falha de rede ao enviar anamnese.', err);
      return { status: 'error', message: this._messages.networkError };
    }

    let data = null;
    try {
      data = await res.json();
    } catch (_err) {
      // resposta não é JSON válido — tratado abaixo como erro
    }

    if (!data) {
      console.error(`AnamneseService: resposta inesperada do endpoint (HTTP ${res.status}).`);
      return { status: 'error', message: this._messages.unexpectedResponse };
    }

    if (data.success && data.status === 'approved') {
      return { status: 'approved', anamneseId: data.anamnese_id };
    }
    if (data.success && data.status === 'rejected') {
      return { status: 'rejected', message: data.message || this._messages.rejectedDefault };
    }
    return { status: 'error', message: data.error || this._messages.genericError };
  }
}

/* ─── AnamneseAttachment ──────────────────────────────────────────────────── */

const ATTACHMENT_MIME_BY_EXT = {
  '.pdf': ['application/pdf'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.png': ['image/png'],
};

class AnamneseAttachment {
  constructor(panel) {
    this._enabled    = panel.dataset.attachmentEnabled === 'true';
    this._required   = panel.dataset.attachmentRequired === 'true';
    this._accept     = (panel.dataset.attachmentAccept || '')
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    this._maxSizeMb  = parseFloat(panel.dataset.attachmentMaxSizeMb) || 10;
    // Já vêm prontos do Liquid (t filter) com a lista de formatos/tamanho
    // máximo interpolados — nada fixo aqui, ver data-msg-attachment-* no panel.
    this._messages = {
      invalidFormat: panel.dataset.msgAttachmentInvalidFormat,
      invalidMime:   panel.dataset.msgAttachmentInvalidMime,
      tooLarge:      panel.dataset.msgAttachmentTooLarge,
    };

    this._trigger = panel.querySelector('[data-anamnesis-upload-trigger]');
    this._input   = panel.querySelector('[data-anamnesis-file-input]');
    this._nameEl  = panel.querySelector('[data-anamnesis-file-name]');
    this._errorEl = panel.querySelector('[data-anamnesis-file-error]');

    this._file      = null;
    this._onChange  = null;

    if (!this._enabled) return;

    this._onTriggerClick = () => this._input?.click();
    this._onInputChange  = () => this._handleChange();

    this._trigger?.addEventListener('click', this._onTriggerClick);
    this._input?.addEventListener('change', this._onInputChange);
  }

  onChange(cb) { this._onChange = cb; }

  _handleChange() {
    const file = this._input?.files?.[0] || null;
    this._setError('');

    if (!file) {
      this._file = null;
      this._syncUi();
      this._onChange?.();
      return;
    }

    const lowerName = file.name.toLowerCase();
    const extOk = this._accept.length === 0 || this._accept.some(ext => lowerName.endsWith(ext));
    if (!extOk) {
      this._setError(this._messages.invalidFormat);
      this._reject();
      return;
    }

    // MIME quando possível — nem todo navegador/SO reporta file.type, então só
    // reprovamos se o MIME foi informado E não bate com nenhuma extensão aceita.
    if (file.type) {
      const matchedExt = this._accept.find(ext => lowerName.endsWith(ext));
      const expectedMimes = matchedExt ? ATTACHMENT_MIME_BY_EXT[matchedExt] : null;
      if (expectedMimes && !expectedMimes.includes(file.type)) {
        this._setError(this._messages.invalidMime);
        this._reject();
        return;
      }
    }

    if (file.size > this._maxSizeMb * 1024 * 1024) {
      this._setError(this._messages.tooLarge);
      this._reject();
      return;
    }

    this._file = file;
    this._syncUi();
    this._onChange?.();
  }

  _reject() {
    this._file = null;
    if (this._input) this._input.value = '';
    this._syncUi();
    this._onChange?.();
  }

  _syncUi() {
    if (!this._nameEl) return;
    if (this._file) {
      this._nameEl.textContent = this._file.name;
      this._nameEl.hidden = false;
    } else {
      this._nameEl.textContent = '';
      this._nameEl.hidden = true;
    }
  }

  _setError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message;
    this._errorEl.hidden = !message;
  }

  /** true quando não há pendência que impeça o envio do formulário. */
  isValid() {
    if (!this._enabled) return true;
    if (this._required && !this._file) return false;
    return true;
  }

  getFile() {
    return this._enabled ? this._file : null;
  }

  /** Elemento pra rolar até quando o anexo faltando barra o envio. */
  getScrollTarget() {
    return this._trigger || null;
  }

  reset() {
    this._file = null;
    if (this._input) this._input.value = '';
    this._setError('');
    this._syncUi();
  }

  destroy() {
    this._trigger?.removeEventListener('click', this._onTriggerClick);
    this._input?.removeEventListener('change', this._onInputChange);
  }
}

/* ─── AnamneseQuestion ────────────────────────────────────────────────────── */

const CONSENT_QUESTION_ID = 'consent';

const ANSWER_GROUP_CLASS = 'anamnesis-answer-group';
const ANSWER_BUTTON_CLASS = 'anamnesis-answer';

const QUESTION_RENDERERS = {
  radio(data, notify) {
    const group = document.createElement('div');
    group.className = ANSWER_GROUP_CLASS;
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', data.label || '');

    let selected = null;
    const buttons = (data.options || []).map(optionLabel => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = ANSWER_BUTTON_CLASS;
      btn.textContent = optionLabel;
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', () => {
        selected = optionLabel;
        buttons.forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
        notify();
      });
      group.appendChild(btn);
      return btn;
    });

    return {
      el: group,
      api: {
        isAnswered: () => selected !== null,
        getAnswer: () => selected,
        reset: () => {
          selected = null;
          buttons.forEach(b => b.setAttribute('aria-pressed', 'false'));
        },
      },
    };
  },

  checkbox(data, notify) {
    const group = document.createElement('div');
    group.className = ANSWER_GROUP_CLASS;
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', data.label || '');

    const selected = new Set();
    const options = data.options && data.options.length ? data.options : [data.label];
    const boxes = options.map(optionLabel => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = ANSWER_BUTTON_CLASS;
      btn.textContent = optionLabel;
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', () => {
        if (selected.has(optionLabel)) {
          selected.delete(optionLabel);
          btn.setAttribute('aria-pressed', 'false');
        } else {
          selected.add(optionLabel);
          btn.setAttribute('aria-pressed', 'true');
        }
        notify();
      });
      group.appendChild(btn);
      return btn;
    });

    return {
      el: group,
      api: {
        isAnswered: () => selected.size > 0,
        getAnswer: () => Array.from(selected),
        reset: () => {
          selected.clear();
          boxes.forEach(b => b.setAttribute('aria-pressed', 'false'));
        },
      },
    };
  },

};

class AnamneseQuestion {
  constructor(data) {
    this.questionId    = data.question_id;
    this.required      = !!data.required;
    // Exposto para TODA pergunta (decisão explícita — ver cabeçalho de
    // snippets/anamnesis-modal.liquid): pra "consent" alimenta o gate de
    // bloqueio (AnamneseForm._syncBlockedState); pra qualquer outra, só o
    // aviso consultivo não-bloqueante (flagsFlow) — nunca decide aprovação
    // real, isso continua exclusivamente no backend.
    this.approvedAnswer = data.approved_answer || null;
    this._onChange  = null;
    this.el = this._render(data);
  }

  _render(data) {
    const wrapper = document.createElement('div');
    wrapper.className = 'anamnesis-toggle anamnesis-question';
    wrapper.dataset.questionId = this.questionId || '';

    const text = document.createElement('p');
    text.className = 'anamnesis-question__text';
    text.innerHTML = data.label || '';
    wrapper.appendChild(text);

    const renderer = QUESTION_RENDERERS[data.type] ? data.type : 'radio';
    const { el: fieldEl, api } = QUESTION_RENDERERS[renderer](data, () => this._onChange?.());
    wrapper.appendChild(fieldEl);
    this._api = api;

    return wrapper;
  }

  onChange(cb) { this._onChange = cb; }
  isAnswered() { return this._api.isAnswered(); }
  getAnswer()  { return this._api.getAnswer(); }
  reset()      { this._api.reset(); }

  /** Destaque visual (mesmo tratamento data-invalid da declaração) quando obrigatória e não respondida. */
  setInvalid(invalid) { this.el.dataset.invalid = String(!!invalid); }

  /** true quando a pergunta já foi respondida e a resposta NÃO bate com approvedAnswer. */
  _mismatchesApproved() {
    if (!this.approvedAnswer) return false;
    if (!this.isAnswered()) return false;
    const normalize = (v) => String(v).trim().toLowerCase();
    const answer = this.getAnswer();
    if (Array.isArray(answer)) return !answer.some(a => normalize(a) === normalize(this.approvedAnswer));
    return normalize(answer) !== normalize(this.approvedAnswer);
  }

  /** Só tem efeito de verdade pra question_id === "consent" (checado por quem chama, AnamneseForm._syncBlockedState). */
  blocksFlow() { return this._mismatchesApproved(); }

  /** Aviso consultivo não-bloqueante — vale pra qualquer pergunta. */
  flagsFlow() { return this._mismatchesApproved(); }
}

/* ─── AnamneseForm ────────────────────────────────────────────────────────── */

class AnamneseForm {
  constructor(panel) {
    this._panel = panel;
    this._listEl = panel.querySelector('[data-anamnesis-questions]');
    this._questions = this._loadQuestions(panel);
    this._questionInstances = [];
    this._buildQuestionDom();

    this._declarationRequired = panel.dataset.declarationRequired === 'true';
    this._declarationWrapper  = panel.querySelector('[data-anamnesis-declaration]');
    this._declarationCheckbox = panel.querySelector('[data-anamnesis-declaration] input[type="checkbox"]');

    this._attachment = new AnamneseAttachment(panel);

    this._statusCount = panel.querySelector('[data-anamnesis-status-count]');
    this._statusBadge = panel.querySelector('[data-anamnesis-status-badge]');
    this._progress    = panel.querySelector('[data-anamnesis-progress]');

    // Gate de consentimento (hard block): esconde as outras perguntas e
    // extras, mostra a mensagem logo abaixo do card da pergunta (dentro do
    // scroll, não no rodapé fixo — quem troca "Validar respostas" por
    // "Fechar" no rodapé é o AnamneseModal, via isBlocked()).
    this._blockedMessageEl = panel.querySelector('[data-anamnesis-blocked-message]');
    // Já vem pronto do Liquid (metaobject block_message, ou o texto padrão do
    // locale quando o merchant deixa em branco — ver anamnesis-modal.liquid).
    this._blockMessage     = panel.dataset.blockMessage;
    this._blockedQuestion  = null;

    // Extras (status + declaração + anexo, rolam junto das perguntas) — some
    // quando bloqueado. O rodapé fixo (mensagem + botão) nunca some inteiro;
    // só troca o conteúdo do botão (ver AnamneseModal).
    this._extrasEl = panel.querySelector('[data-anamnesis-extras]');

    // Aviso consultivo não-bloqueante + erros de validação, no mesmo slot visual.
    this._messageEl      = panel.querySelector('[data-anamnesis-message]');
    // Já vem pronto do Liquid (metaobject advisory_message, ou o texto padrão
    // do locale quando o merchant deixa em branco).
    this._advisoryMessage = panel.dataset.advisoryMessage;
    // Erros de validação/aviso: todos vêm de locales/*.json via t filter,
    // nunca fixos aqui — ver data-msg-* em [data-anamnesis-panel].
    this._messages = {
      missingRequired:      panel.dataset.msgMissingRequired,
      declarationRequired:  panel.dataset.msgDeclarationRequired,
      attachmentRequired:   panel.dataset.msgAttachmentRequired,
    };
    this._statusCountTemplate = panel.dataset.statusCountTemplate || '';
    this._statusPendingLabel  = panel.dataset.statusPending;
    this._statusCompleteLabel = panel.dataset.statusComplete;
    this._submitAttempted = false;

    this._onChange = null;
    this._bindChange();
    this._notifyChange();
  }

  /** Lê o JSON seguro (sem approved_answer) embutido pelo Liquid. */
  _loadQuestions(panel) {
    const script = panel.querySelector('[data-anamnesis-questions-data]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error('AnamneseForm: JSON de perguntas inválido.', err);
      return [];
    }
  }

  _buildQuestionDom() {
    if (!this._listEl) return;
    this._questions
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .forEach(data => {
        const instance = new AnamneseQuestion(data);
        this._questionInstances.push(instance);
        this._listEl.appendChild(instance.el);
      });
  }

  _bindChange() {
    this._questionInstances.forEach(q => q.onChange(() => this._notifyChange()));
    this._onDeclarationChange = () => this._notifyChange();
    this._declarationCheckbox?.addEventListener('change', this._onDeclarationChange);
    this._attachment.onChange(() => this._notifyChange());
  }

  onChange(cb) { this._onChange = cb; }

  _notifyChange() {
    this._syncBlockedState();
    this._updateCounter();
    this._renderMessage();
    this._onChange?.();
  }

  _syncBlockedState() {
    const consentQuestion = this._questionInstances.find(
      q => String(q.questionId || '').trim().toLowerCase() === CONSENT_QUESTION_ID
    );
    this._blockedQuestion = (consentQuestion && consentQuestion.blocksFlow()) ? consentQuestion : null;

    this._questionInstances.forEach(q => {
      q.el.hidden = !!this._blockedQuestion && q !== this._blockedQuestion;
    });
    if (this._extrasEl) this._extrasEl.hidden = !!this._blockedQuestion;
    if (this._blockedMessageEl) {
      this._blockedMessageEl.hidden = !this._blockedQuestion;
      if (this._blockedQuestion) this._blockedMessageEl.textContent = this._blockMessage;
    }
  }

  isBlocked() { return !!this._blockedQuestion; }

  _updateCounter() {
    const total = this._questionInstances.length;
    const answered = this._questionInstances.filter(q => q.isAnswered()).length;
    const allAnswered = total > 0 && answered === total;

    if (this._progress) {
      const pct = total ? Math.round((answered / total) * 100) : 0;
      this._progress.style.width = `${pct}%`;
    }
    if (this._statusCount && this._statusCountTemplate) {
      this._statusCount.textContent = this._statusCountTemplate.replace('[[answered]]', answered);
    }
    if (this._statusBadge) {
      this._statusBadge.textContent = allAnswered ? this._statusCompleteLabel : this._statusPendingLabel;
      this._statusBadge.dataset.complete = String(allAnswered);
    }
  }

  /**
   * Decide o que mostrar no slot de mensagem — tudo aqui só é avaliado depois
   * de uma tentativa de envio (_submitAttempted), nunca reativamente enquanto
   * o cliente ainda está respondendo. Em ordem de prioridade:
   *   1. erro de validação (pergunta obrigatória, declaração, anexo)
   *   2. aviso de resposta incorreta — todas as perguntas respondidas e pelo
   *      menos uma não bate com approved_answer. Bloqueia o envio: nenhuma
   *      request é feita ao clicar, só o texto aparece acima do botão.
   *   3. nada
   */
  _renderMessage() {
    if (this._declarationWrapper) this._declarationWrapper.dataset.invalid = 'false';
    this._questionInstances.forEach(q => q.setInvalid(false));

    let message = null;
    this._scrollTarget = null;

    if (this._submitAttempted) {
      const missingRequired = this._questionInstances.filter(q => q.required && !q.isAnswered());
      if (missingRequired.length > 0) {
        message = { type: 'error', text: this._messages.missingRequired };
        missingRequired.forEach(q => q.setInvalid(true));
        this._scrollTarget = missingRequired[0].el;
      } else if (this._declarationRequired && !(this._declarationCheckbox && this._declarationCheckbox.checked)) {
        message = { type: 'error', text: this._messages.declarationRequired };
        if (this._declarationWrapper) this._declarationWrapper.dataset.invalid = 'true';
        this._scrollTarget = this._declarationWrapper;
      } else if (!this._attachment.isValid()) {
        message = { type: 'error', text: this._messages.attachmentRequired };
        this._scrollTarget = this._attachment.getScrollTarget();
      } else {
        const allAnswered = this._questionInstances.every(q => q.isAnswered());
        const mismatched = allAnswered && this._questionInstances.some(q => q.flagsFlow());
        if (mismatched) {
          message = { type: 'advisory', text: this._advisoryMessage, blocks: true };
        }
      }
    }

    this._blocksSubmit = !!message && (message.type === 'error' || message.blocks === true);

    if (!this._messageEl) return;
    if (!message) {
      this._messageEl.hidden = true;
      this._messageEl.textContent = '';
      this._messageEl.dataset.type = '';
      return;
    }
    this._messageEl.hidden = false;
    this._messageEl.textContent = message.text;
    this._messageEl.dataset.type = message.type;
  }

  /**
   * Valida e, se algo faltar ou alguma resposta não bater com approved_answer,
   * exibe o aviso correspondente no slot de mensagem acima do botão (chamado
   * a partir daqui em diante, reativamente, até o problema ser corrigido).
   * Quando falta preencher algo (pergunta obrigatória, declaração ou anexo),
   * também rola até o item pendente dentro do painel.
   * @returns {boolean} true quando pronto para seguir para o envio real.
   */
  validate() {
    this._submitAttempted = true;
    this._renderMessage();
    if (this._blocksSubmit && this._scrollTarget && typeof this._scrollTarget.scrollIntoView === 'function') {
      this._scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return !this._blocksSubmit;
  }

  getAnswers() {
    const out = {};
    this._questionInstances.forEach(q => {
      if (q.questionId) out[q.questionId] = q.getAnswer();
    });
    return out;
  }

  getDeclaration() {
    return this._declarationCheckbox ? this._declarationCheckbox.checked : true;
  }

  getAttachmentFile() {
    return this._attachment.getFile();
  }

  reset() {
    this._questionInstances.forEach(q => q.reset());
    if (this._declarationCheckbox) this._declarationCheckbox.checked = false;
    this._attachment.reset();
    this._submitAttempted = false;
    this._notifyChange();
  }

  destroy() {
    this._declarationCheckbox?.removeEventListener('change', this._onDeclarationChange);
    this._attachment.destroy();
  }
}

/* ─── AnamneseModal ───────────────────────────────────────────────────────── */

class AnamneseModal {
  constructor(panel) {
    this._panel = panel;
    const drawerEl = panel.closest('[data-drawer]');
    this.name = drawerEl?.dataset.drawer || null;
    this.productId = panel.dataset.productId || null;

    this._form = new AnamneseForm(panel);
    this._service = new AnamneseService(panel.dataset.submitUrl, {
      networkError:      panel.dataset.msgNetworkError,
      unexpectedResponse: panel.dataset.msgUnexpectedResponse,
      rejectedDefault:    panel.dataset.msgRejectedDefault,
      genericError:       panel.dataset.msgGenericError,
    });

    this._submitBtn = panel.querySelector('[data-anamnesis-submit]');
    this._closeBtn  = panel.querySelector('[data-anamnesis-close]');
    this._phaseEls = {
      form: panel.querySelector('[data-anamnesis-phase="form"]'),
      submitting: panel.querySelector('[data-anamnesis-phase="submitting"]'),
      rejected: panel.querySelector('[data-anamnesis-phase="rejected"]'),
      error: panel.querySelector('[data-anamnesis-phase="error"]'),
      approved: panel.querySelector('[data-anamnesis-phase="approved"]'),
    };
    this._rejectedMessageEl = panel.querySelector('[data-anamnesis-rejected-message]');
    this._errorMessageEl = panel.querySelector('[data-anamnesis-error-message]');
    this._retryBtn = panel.querySelector('[data-anamnesis-retry]');
    // Mesmas mensagens padrão passadas ao AnamneseService — nada fixo aqui.
    this._rejectedDefaultMessage = panel.dataset.msgRejectedDefault;
    this._errorDefaultMessage    = panel.dataset.msgGenericError;

    this._variantId = null;
    this._onApproved = null;
    this._isSubmitting = false;

    this._onSubmitClick = () => this._handleSubmit();
    this._onRetryClick  = () => this._setPhase('form');
    this._onDrawerClose = ({ name }) => { if (name === this.name) this._handleClose(); };

    this._submitBtn?.addEventListener('click', this._onSubmitClick);
    this._retryBtn?.addEventListener('click', this._onRetryClick);
    this._offDrawerClose = window.DrawerManager?.on('drawer:close', this._onDrawerClose);

    this._form.onChange(() => this._syncSubmitState());
    this._setPhase('form');
  }

  /**
   * Abre o modal para a variante informada (lida no momento do submit do
   * Product Form, nunca em cache do carregamento inicial da página).
   * @param {{ variantId: string, onApproved: (properties: { _anamnese_id: string, _anamnese_status: string }) => void }} options
   */
  open({ variantId, onApproved }) {
    this._variantId = variantId;
    this._onApproved = onApproved;
    this._setPhase('form');
    window.DrawerManager?.open(this.name);
  }

  _handleClose() {
    // Sempre reseta ao fechar — sucesso, recusa do backend, X, overlay ou ESC —
    // assim uma nova tentativa de compra começa com o questionário limpo, e um
    // fechamento sem aprovação nunca deixa onApproved pendente para o futuro.
    this._onApproved = null;
    this._form.reset();
    this._setPhase('form');
  }

  _syncSubmitState() {
    const blocked = this._form.isBlocked();
    // O botão fica clicável mesmo com o formulário incompleto — validate()
    // mostra o erro certo inline no clique (ver AnamneseForm._renderMessage).
    // Quando bloqueado, "Validar respostas" some e "Fechar" toma o lugar —
    // o rodapé fixo em si nunca some inteiro, só troca de botão.
    if (this._submitBtn) {
      this._submitBtn.hidden   = blocked;
      this._submitBtn.disabled = this._isSubmitting;
    }
    if (this._closeBtn) this._closeBtn.hidden = !blocked;
  }

  /**
   * Sem backend hoje: validate() já barrou qualquer resposta que não bata
   * com approved_answer (ver AnamneseForm._renderMessage), então uma
   * submissão que chega aqui já está aprovada — fecha o modal e adiciona ao
   * carrinho direto, sem tela intermediária. `this._service` (POST para o
   * App Proxy, ver docs/anamnese-backend.md) fica pronto para quando existir
   * um backend real; é só voltar a chamá-lo aqui antes de aprovar.
   */
  _handleSubmit() {
    if (this._form.isBlocked()) return;
    if (!this._form.validate()) return; // erro/resposta incorreta exibido inline, não envia

    const anamneseId = 'local-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const properties = {
      _anamnese_id: anamneseId,
      _anamnese_status: 'approved',
    };
    const callback = this._onApproved;
    window.DrawerManager?.close(this.name); // → _handleClose() reseta o form
    callback?.(properties);
  }

  _setPhase(phase, message) {
    Object.entries(this._phaseEls).forEach(([key, el]) => {
      if (el) el.hidden = key !== phase;
    });

    if (phase === 'rejected' && this._rejectedMessageEl) {
      this._rejectedMessageEl.textContent = message || this._rejectedDefaultMessage;
    }
    if (phase === 'error' && this._errorMessageEl) {
      this._errorMessageEl.textContent = message || this._errorDefaultMessage;
    }

    this._syncSubmitState();
  }

  destroy() {
    this._submitBtn?.removeEventListener('click', this._onSubmitClick);
    this._retryBtn?.removeEventListener('click', this._onRetryClick);
    this._offDrawerClose?.();
    this._form.destroy();
  }
}

window.AnamneseModal = AnamneseModal;
